// The ONE place the UI turns a role key into a human label. Mirrors the backend role enum and the
// labels in actionables/engine.js. Used by the Rules picker and anywhere a role-shared actionable
// names its owner.
//
// Post-RBAC: the 7 live roles are admin / warehouse-manager / warehouse-member / catalog-manager /
// catalog-member / data-tech / photography. The retired super-admin / viewer keys are kept as labels only (NR-8,
// back-compat) so any lingering stored value still renders a human label rather than a raw key.
export const ROLE_LABELS: Record<string, string> = {
    admin: 'Admin',
    'warehouse-manager': 'Warehouse Manager',
    'warehouse-member': 'Warehouse Member',
    'catalog-manager': 'Catalog Manager',
    'catalog-member': 'Catalog Member',
    'data-tech': 'Data & Tech',
    photography: 'Photography',
    // Retired keys — label-only back-compat.
    'super-admin': 'Super Admin',
    viewer: 'Viewer',
};

// The known live role keys, in display order — handy for building a role picker from the shared map.
export const ROLE_KEYS: string[] = [
    'admin',
    'warehouse-manager',
    'warehouse-member',
    'catalog-manager',
    'catalog-member',
    'data-tech',
    'photography',
];

export const roleLabel = (role: string): string => ROLE_LABELS[role] || role;
export const roleLabels = (roles: string[]): string => (roles || []).map(roleLabel).join(' + ');

// The ONE source of truth for role colours across the Manage Users UI — avatar rings, role
// badges/pills and the permissions matrix all read from here so a role's colour can never
// diverge between surfaces. LEAF data (plain strings only), no React / theme imports, so this
// stays import-cycle-safe (no back-edge into App.tsx).
export interface RoleColor {
    main: string; // the solid accent (ring, dot)
    bg: string; // subtle tinted background for pills/badges
    text: string; // readable text colour on the tinted background
}

const FALLBACK_ROLE_COLOR: RoleColor = { main: '#8A8078', bg: '#f2efe8', text: '#6b6258' };

export const ROLE_COLORS: Record<string, RoleColor> = {
    admin: { main: '#DC5829', bg: '#fcece4', text: '#b4431d' },
    'warehouse-manager': { main: '#2563eb', bg: '#e8f0fe', text: '#1d4ed8' },
    'warehouse-member': { main: '#0891b2', bg: '#e2f5f8', text: '#0e7490' },
    'catalog-manager': { main: '#7c3aed', bg: '#f1eafe', text: '#6d28d9' },
    'catalog-member': { main: '#4f46e5', bg: '#eaeafe', text: '#4338ca' },
    'data-tech': { main: '#15803d', bg: '#e6f4ea', text: '#15803d' },
    photography: { main: '#db2777', bg: '#fce7f1', text: '#be185d' },
};

export const roleColor = (role: string): RoleColor => ROLE_COLORS[role] || FALLBACK_ROLE_COLOR;
